import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import './SubTotalSection.scss';
import { formatAmountWithLocale } from '../helper/common.helper';
import {
  PENSION_MIN_SEK,
  getPensionMaxSek,
  getPensionAmountOptions,
  validatePensionContribution,
} from '../utils/calculation.utils';

interface SubTotalSectionProps {
  subTotal: number;
  pensionPay: number;
  holidayPay: number;
  grandTotal: number;
  currency?: string;
  onPensionChange: (value: number) => void;
  onHolidayChange: (value: number) => void;
  t: (key: string) => string;
  isPensionEnabled: boolean;
  isHolidayEnabled: boolean;
  pensionOptions?: number[];
  holidayOptions?: number[];
  calculatedSubAmount?: any;
  language?: string;
  vat?: number;
  vatAmount?: number;
  isCalculateInvoiceLoading?: boolean;
  friendUserID?: string;
  onShowCalculation?: () => void;
  isCalculationLoading?: boolean;
}

export const SubTotalSection: React.FC<SubTotalSectionProps> = ({
  subTotal,
  pensionPay,
  holidayPay,
  grandTotal,
  currency = 'Kr',
  onPensionChange,
  onHolidayChange,
  t,
  isPensionEnabled,
  isHolidayEnabled,
  pensionOptions = [1000, 1500, 2000, 2500, 3000],
  holidayOptions = [12, 20, 30, 40, 50],
  calculatedSubAmount,
  language = 'en',
  vat = 25,
  vatAmount = 0,
  isCalculateInvoiceLoading = false,
  friendUserID,
  onShowCalculation,
  isCalculationLoading = false,
}) => {
  const { i18n } = useTranslation();
  const vatPercentage = vat;
  const [isPensionDropdownOpen, setIsPensionDropdownOpen] = useState(false);
  const [isHolidayDropdownOpen, setIsHolidayDropdownOpen] = useState(false);
  const [customPension, setCustomPension] = useState('');
  const [pensionError, setPensionError] = useState('');

  // The pension dropdown is portaled to <body> (fixed position) so it isn't
  // hard-clipped by the freelancer card's `overflow: hidden` (used for its
  // rounded corners) when the menu is taller than the remaining card space.
  const pensionWrapperRef = useRef<HTMLDivElement>(null);
  const [pensionMenuRect, setPensionMenuRect] = useState<{ top: number; left: number; width: number } | null>(null);

  // The trigger itself is narrow, which clipped the "Custom amount" placeholder —
  // widen the menu (min 280px) and center it under the trigger (rather than
  // left-aligning it, which made the wider menu drift off to the trigger's
  // right), clamped so it never runs past the edges of the viewport.
  const PENSION_MENU_MIN_WIDTH = 280;

  const recalcPensionMenuRect = () => {
    const el = pensionWrapperRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
    // Never request more width than the viewport can actually give us (minus margins),
    // so the menu can't push the page into horizontal scroll.
    const maxAvailableWidth = Math.max(200, viewportWidth - 16);
    const width = Math.min(Math.max(rect.width, PENSION_MENU_MIN_WIDTH), maxAvailableWidth);
    const triggerCenter = rect.left + rect.width / 2;
    const left = Math.max(8, Math.min(triggerCenter - width / 2, viewportWidth - width - 8));
    setPensionMenuRect({ top: rect.bottom + 4, left, width });
  };

  const togglePensionDropdown = () => {
    if (!isPensionDropdownOpen) {
      recalcPensionMenuRect();
      // The active amount wasn't picked from the preset list — it was typed into
      // "Custom amount" and added, so show it there again by default instead of
      // an empty placeholder.
      if (pensionPay && !dynamicPensionOptions.includes(pensionPay)) {
        setCustomPension(String(pensionPay));
      } else {
        setCustomPension('');
      }
      setPensionError('');
    } else {
      // Closing without clicking "Add" — drop whatever was typed so it doesn't
      // reappear, unadded, the next time the dropdown is opened.
      setCustomPension('');
      setPensionError('');
    }
    setIsPensionDropdownOpen((open) => !open);
  };

  useEffect(() => {
    if (!isPensionDropdownOpen) return;
    window.addEventListener('scroll', recalcPensionMenuRect, true);
    window.addEventListener('resize', recalcPensionMenuRect);
    return () => {
      window.removeEventListener('scroll', recalcPensionMenuRect, true);
      window.removeEventListener('resize', recalcPensionMenuRect);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPensionDropdownOpen]);

  // Get freelancer-specific data
  const freelancerData = calculatedSubAmount?.response?.freelancersData?.find(
    (fd: any) => fd.createdBy?.toString() === friendUserID?.toString()
  );

  // Occupational pension (Induli) limits: min 1,000 SEK, max = min(49,000, 25% of gross).
  // Gross salary comes from the already-calculated per-freelancer value (grossInSek) —
  // reused, not recomputed, so the invoice calculation is untouched.
  const pensionGrossSek: number = Number(freelancerData?.grossInSek) || 0;
  const pensionMaxSek: number = getPensionMaxSek(pensionGrossSek);
  const dynamicPensionOptions: number[] =
    getPensionAmountOptions(pensionGrossSek).length > 0
      ? getPensionAmountOptions(pensionGrossSek)
      : (pensionOptions || []);

  const applyCustomPension = () => {
    const res = validatePensionContribution(parseFloat(customPension), pensionGrossSek);
    if (res.errorKey) {
      const maxTxt = `${formatAmountWithLocale(res.max, 'SEK', i18n.language)}`;
      const msg =
        res.errorKey === 'PENSION_MIN_NOT_MET'
          ? (t('PENSION_MIN_NOT_MET_MSG') || 'Your salary allows less than the 1,000 SEK minimum, so a pension contribution can’t be added.')
          : res.errorKey === 'PENSION_BELOW_MIN'
          ? (t('PENSION_BELOW_MIN_MSG') || `The minimum contribution is ${PENSION_MIN_SEK.toLocaleString()} SEK.`)
          : (t('PENSION_ABOVE_MAX_MSG') || 'Amount is above the maximum allowed') + ` (${maxTxt}).`;
      setPensionError(msg);
      return;
    }
    onPensionChange(res.amount);
    setPensionError('');
    setCustomPension('');
    setIsPensionDropdownOpen(false);
  };

  // Negative payout for this freelancer: the invoice amount can't cover fees/tax
  const netInSek: number = freelancerData?.netInSek || freelancerData?.net || 0;
  const isPayoutNegative = netInSek < 0 || ((freelancerData?.setTotalAmount ?? 0) > 0 && netInSek <= 0);

  // Get currency from API response or fall back to prop
  const displayCurrency = freelancerData?.currencyName || calculatedSubAmount?.response?.totalCalculation?.currencyName || currency || 'SEK';

  return (
    <div className="subtotal-wrapper">
    <div className="subtotal-section">
      <div className="subtotal-row">
        <span className="subtotal-label">{t('SUB_TOTAL') || 'Sub Total'} :</span>
        <div className="total-values">
          <div className="subtotal-value">
            {isCalculateInvoiceLoading ? (
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            ) : (
              formatAmountWithLocale(
                freelancerData?.amount || 0,
                displayCurrency,
                i18n.language
              )
            )}
          </div>
          <div className="vat-row">
            <span className="vat-value">
              +{vatPercentage}% {t('VAT_LABEL') || 'VAT'} ({freelancerData?.vat > 0 ? formatAmountWithLocale(freelancerData?.vat, displayCurrency, i18n.language) : '0'})
            </span>
          </div>
        </div>
      </div>

      {isPensionEnabled && (
        <div className="input-row" data-tour="tour-pension-fee" id={`iv-pension-fee-${friendUserID}`}>
          <label className="input-label">{t('PENSION_PAY') || 'Pension Pay'} :</label>
          <div className="input-wrapper" ref={pensionWrapperRef} onClick={togglePensionDropdown}>
            <span className="amount-input">{pensionPay || dynamicPensionOptions[0] || PENSION_MIN_SEK}</span>
            <span className="currency-select">SEK</span>
            <svg className="dropdown-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M5 7.5L10 12.5L15 7.5"
                stroke="currentColor"
                strokeWidth="1.67"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {isPensionDropdownOpen && pensionMenuRect && createPortal(
              <div
                className="dropdown-menu-custom dropdown-menu-portal"
                style={{ position: 'fixed', top: pensionMenuRect.top, left: pensionMenuRect.left, right: 'auto', width: pensionMenuRect.width }}
              >
                {/* Custom amount — pinned to the top of the single scroll area (sticky,
                    not a separate nested scroll box) so it's always visible AND every
                    preset option below — including the last one — stays reachable by
                    scrolling this one container. Validated against min 3,000 and
                    max (25% of gross, capped 49,000). */}
                <div className="pension-custom-sticky">
                  <div className="dropdown-item-custom pension-custom-row" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="number"
                      min={PENSION_MIN_SEK}
                      max={pensionMaxSek}
                      className="pension-custom-input"
                      placeholder={t('PENSION_CUSTOM_AMOUNT') || 'Custom amount'}
                      value={customPension}
                      onChange={(e) => { setCustomPension(e.target.value); if (pensionError) setPensionError(''); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyCustomPension(); } }}
                    />
                    <button
                      type="button"
                      className="pension-custom-apply"
                      onClick={applyCustomPension}
                      aria-label={t('ADD') || 'Add'}
                      title={t('ADD') || 'Add'}
                    >
                      <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                        <path d="M10 4V16M4 10H16" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                      </svg>
                    </button>
                  </div>
                  <div className="pension-custom-limits">
                    Min {PENSION_MIN_SEK.toLocaleString()} SEK · Max {pensionMaxSek.toLocaleString()} SEK
                  </div>
                  {pensionError && <div className="pension-custom-error">{pensionError}</div>}
                  <div className="pension-dropdown-divider" />
                </div>

                <div className="pension-options-list">
                  <div className="pension-options-label">{t('SUGGESTED_AMOUNTS') || 'Suggested amounts'}</div>
                  {dynamicPensionOptions.map((value) => {
                    const isSelected = value === (pensionPay || dynamicPensionOptions[0] || PENSION_MIN_SEK);
                    return (
                      <div
                        key={value}
                        className={`dropdown-item-custom${isSelected ? ' selected' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onPensionChange(value);
                          // Picking a preset discards whatever was typed into "Custom
                          // amount" but never submitted (Add not clicked) — otherwise
                          // it would reappear, unadded, next time the dropdown opens.
                          setCustomPension('');
                          setPensionError('');
                          setIsPensionDropdownOpen(false);
                        }}
                      >
                        <span className="dropdown-item-amount">{value.toLocaleString()}</span>
                        <span className="dropdown-item-currency">SEK</span>
                        {isSelected && (
                          <svg className="dropdown-item-check" width="16" height="16" viewBox="0 0 20 20" fill="none">
                            <path d="M4 10.5L8 14.5L16 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>,
              document.body
            )}
          </div>
        </div>
      )}

      {isHolidayEnabled && (
        <div className="input-row" data-tour="tour-holiday-fee" id={`iv-holiday-fee-${friendUserID}`}>
          <label className="input-label">{t('HOLIDAY_PAY') || 'Holiday Pay'} :</label>
          <div className="input-wrapper" onClick={() => setIsHolidayDropdownOpen(!isHolidayDropdownOpen)}>
            <span className="amount-input">{holidayPay || holidayOptions[0]}</span>
            <span className="currency-select">%</span>
            <svg className="dropdown-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M5 7.5L10 12.5L15 7.5"
                stroke="currentColor"
                strokeWidth="1.67"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {isHolidayDropdownOpen && (
              <div className="dropdown-menu-custom">
                {holidayOptions.map((value) => (
                  <div
                    key={value}
                    className="dropdown-item-custom"
                    onClick={(e) => {
                      e.stopPropagation();
                      onHolidayChange(value);
                      setIsHolidayDropdownOpen(false);
                    }}
                  >
                    {value}%
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grand-total-row">
        <span className="grand-total-label">{t('GRAND_TOTAL') || 'Grand Total'} :</span>
        <span className="grand-total-value">
          {isCalculateInvoiceLoading ? (
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          ) : (
            formatAmountWithLocale(
              freelancerData?.amountIncludingVat || 0,
              displayCurrency,
              i18n.language
            )
          )}
        </span>
      </div>

      {/* Payout would be negative: the amount can't cover the Invozio fee, social fees and tax */}
      {!isCalculateInvoiceLoading && isPayoutNegative && (
        <div
          role="alert"
          style={{
            marginTop: 12,
            padding: '10px 14px',
            borderRadius: 12,
            background: 'rgba(239, 68, 68, 0.08)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            color: '#b91c1c',
            fontSize: 13,
            fontWeight: 600,
            lineHeight: 1.4,
          }}
        >
          {t('PAYOUT_NEGATIVE_ERROR')}
        </div>
      )}

      {/* Net salary this freelancer receives ("Amount paid to your account") */}
      <div className={`net-salary-row${isPayoutNegative ? ' payout-negative' : ''}`}>
        <span className="net-salary-label">{t('AMOUNT_PAID_TO_YOUR_ACCOUNT') || 'Amount paid to your account'} :</span>
        <span className="net-salary-value">
          {isCalculateInvoiceLoading ? (
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          ) : (
            // Salary is always paid out in SEK, regardless of the invoice currency —
            // show the SEK net amount (netInSek) with a fixed SEK label.
            // formatAmountWithLocale drops the sign, so re-add it for a negative payout.
            `${netInSek < 0 ? '-' : ''}${formatAmountWithLocale(netInSek, 'SEK', i18n.language)}`
          )}
        </span>
      </div>

      <button 
        className="calculation-link" 
        onClick={onShowCalculation}
        disabled={!onShowCalculation || isCalculationLoading || isCalculateInvoiceLoading}
      >
        {isCalculationLoading ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            {t('LOADING') || 'Loading...'}
          </>
        ) : (
          <>
            {t('SEE_HOW_WE_CALCULATED') || 'See how we calculated'}
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M6 12L10 8L6 4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </>
        )}
      </button>
    </div>
    </div>
  );
};
